import api from './api';
import {
  Ticket,
  TicketListItem,
  TicketMessage,
  TicketStats,
  TicketStatus,
  TicketFilter,
  CreateTicketRequest,
  UpdateTicketStatusRequest,
  AssignTicketRequest,
  UpdateTicketPriorityRequest,
  CreateTicketMessageRequest,
  ApiResponse,
} from '../types/ticket';

export const ticketService = {
  /**
   * Get all tickets with optional filters
   */
  async getAll(filter?: TicketFilter): Promise<ApiResponse<TicketListItem[]>> {
    const params = new URLSearchParams();

    if (filter?.tenantId) params.append('tenantId', filter.tenantId);
    if (filter?.status !== undefined) params.append('status', String(filter.status));
    if (filter?.priority !== undefined) params.append('priority', String(filter.priority));
    if (filter?.category !== undefined) params.append('category', String(filter.category));
    if (filter?.assignedToUserId) params.append('assignedToUserId', filter.assignedToUserId);

    const queryString = params.toString();
    const url = queryString ? `/api/v1/tickets?${queryString}` : '/api/v1/tickets';

    const { data } = await api.get<ApiResponse<TicketListItem[]>>(url);
    return data;
  },

  /**
   * Get ticket by ID with messages
   */
  async getById(id: string): Promise<ApiResponse<Ticket>> {
    const { data } = await api.get<ApiResponse<Ticket>>(`/api/v1/tickets/${id}`);
    return data;
  },

  /**
   * Get ticket statistics
   */
  async getStats(): Promise<ApiResponse<TicketStats>> {
    const { data } = await api.get<ApiResponse<TicketStats>>('/api/v1/tickets/stats');
    return data;
  },

  /**
   * Create a new ticket (usually for testing/admin purposes)
   */
  async create(request: CreateTicketRequest): Promise<ApiResponse<Ticket>> {
    const { data } = await api.post<ApiResponse<Ticket>>('/api/v1/tickets', request);
    return data;
  },

  /**
   * Update ticket status
   */
  async updateStatus(id: string, request: UpdateTicketStatusRequest): Promise<ApiResponse<null>> {
    const { data } = await api.patch<ApiResponse<null>>(`/api/v1/tickets/${id}/status`, request);
    return data;
  },

  /**
   * Assign ticket to user
   */
  async assign(id: string, request: AssignTicketRequest): Promise<ApiResponse<null>> {
    const { data } = await api.patch<ApiResponse<null>>(`/api/v1/tickets/${id}/assign`, request);
    return data;
  },

  /**
   * Update ticket priority
   */
  async updatePriority(id: string, request: UpdateTicketPriorityRequest): Promise<ApiResponse<null>> {
    const { data } = await api.patch<ApiResponse<null>>(`/api/v1/tickets/${id}/priority`, request);
    return data;
  },

  /**
   * Add message to ticket
   */
  async addMessage(ticketId: string, request: CreateTicketMessageRequest): Promise<ApiResponse<TicketMessage>> {
    const { data } = await api.post<ApiResponse<TicketMessage>>(`/api/v1/tickets/${ticketId}/messages`, request);
    return data;
  },

  /**
   * Get ticket messages
   */
  async getMessages(ticketId: string): Promise<ApiResponse<TicketMessage[]>> {
    const { data } = await api.get<ApiResponse<TicketMessage[]>>(`/api/v1/tickets/${ticketId}/messages`);
    return data;
  },

  /**
   * Assign ticket to current user (self-assign)
   */
  async assignToMe(id: string, userId: string): Promise<ApiResponse<null>> {
    return this.assign(id, { assignedToUserId: userId });
  },

  /**
   * Unassign ticket
   */
  async unassign(id: string): Promise<ApiResponse<null>> {
    return this.assign(id, { assignedToUserId: null });
  },

  /**
   * Close ticket
   */
  async close(id: string): Promise<ApiResponse<null>> {
    return this.updateStatus(id, { status: TicketStatus.Closed });
  },

  /**
   * Resolve ticket
   */
  async resolve(id: string): Promise<ApiResponse<null>> {
    return this.updateStatus(id, { status: TicketStatus.Resolved });
  },

  /**
   * Reopen ticket (set to Open status)
   */
  async reopen(id: string): Promise<ApiResponse<null>> {
    return this.updateStatus(id, { status: TicketStatus.Open });
  },
};
